import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { IconCheck } from './Icons';

/**
 * ExerciseReward — a brief, celebratory confirmation shown right after a
 * member submits an exercise. It gives a clear "you did it" signal and a small
 * dose of praise before the next step slides in.
 *
 * Psychology: every submitted effort gets acknowledged. The reward is short
 * (~1.5s) so it feels rewarding, never in the way. A different praise line each
 * time keeps it from feeling robotic.
 *
 * Render it conditionally (only while submitting). It picks a praise line once
 * on mount, plays its animation, then calls `onComplete` so the parent can
 * navigate onward.
 */

const PRAISES = [
  'Nailed it!',
  'Beautifully done!',
  'Great work!',
  'Lesson completed!',
  'Crisp and clean.',
  'Completed!',
  'Ideal!',
  'Sharp work!',
];

const ExerciseReward = ({ onComplete, duration = 1500 }) => {
  const prefersReduced = useReducedMotion();
  const praise = useMemo(() => PRAISES[Math.floor(Math.random() * PRAISES.length)], []);

  useEffect(() => {
    const timer = setTimeout(() => onComplete && onComplete(), duration);
    return () => clearTimeout(timer);
  }, [onComplete, duration]);

  const spring = { type: 'spring', stiffness: 320, damping: 22 };

  // Rendered through a portal on <body> so the overlay is centred against the
  // viewport — not the page-transition wrapper, whose CSS transform would
  // otherwise become the containing block for our fixed positioning.
  return createPortal(
    <motion.div
      className="ac-reward"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <motion.div
        className="ac-reward-card"
        initial={prefersReduced ? { opacity: 0 } : { opacity: 0, scale: 0.92, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={prefersReduced ? { duration: 0.2 } : { ...spring, delay: 0.04 }}
      >
        <span className="ac-reward-check-wrap">
          {!prefersReduced && (
            <motion.span
              className="ac-reward-ring"
              initial={{ scale: 0.7, opacity: 0.55 }}
              animate={{ scale: 1.9, opacity: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut', delay: 0.1 }}
            />
          )}
          <motion.span
            className="ac-reward-check"
            initial={prefersReduced ? { opacity: 0 } : { scale: 0, rotate: -12 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={prefersReduced ? { duration: 0.2 } : { ...spring, delay: 0.12 }}
          >
            <IconCheck size={28} />
          </motion.span>
        </span>

        <motion.h3
          className="ac-reward-title"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut', delay: 0.18 }}
        >
          {praise}
        </motion.h3>
        <motion.p
          className="ac-reward-sub"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut', delay: 0.24 }}
        >
          Submitted successfully
        </motion.p>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default ExerciseReward;
